import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  Colors,
  Environment,
  FontFamilies,
  FontSizes,
  Logger,
  MediaQueries,
  Theme,
} from 'common';
import {
  Button,
  Text,
} from 'common/components';
import { WeTicketLogo } from 'common/components/svg';
import { withCommon } from 'common/hocs';

const Container = styled.div`
  padding: 20px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const MaxWidthContainer = styled.div`
  width: 100%;
  max-width: 650px;
`;

const Title = styled.h1`
  font-size: 1.6em;
  margin: 20px 0 10px 0;
  color: ${Theme.Text.Color};
`;

const Divide = styled.div`
  border-bottom: 1px solid ${Theme.Divider.Color};
  margin: 15px 0 15px 0;
  width: 100%;
`;

const EventName = styled.h2`
  margin: 4px 0 0 0;
  font-size: 1.25em;
  font-family: ${FontFamilies.Bold};
  color: ${Theme.Text.Color};
`;

const EventMeta = styled.p`
  margin: 6px 0 0 0;
  font-size: ${FontSizes.Small};
  color: ${Theme.Text.Color};
  opacity: 0.75;
`;

const SectionTitle = styled.h3`
  margin: 22px 0 10px 0;
  font-size: ${FontSizes.Small};
  font-family: ${FontFamilies.Bold};
  color: ${Theme.Text.Color};
`;

const PassList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: ${props => props.$offset ? '18px' : '0'};
`;

const Pass = styled.article`
  background-color: ${Theme.Card.Background};
  border: ${Theme.Card.Border};
  border-radius: ${Theme.Card.BorderRadius};
  box-shadow: ${Theme.Card.BoxShadow};
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const PassHeader = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 16px 12px 16px;
`;

const KindBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 8px 12px;
  background: ${props => props.$kind === 'voucher' ? '#234e70' : Colors.Black};
  color: ${Colors.White};
  font-family: ${FontFamilies.Bold};
  font-size: ${FontSizes.Smaller};
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

const KindCount = styled.span`
  font-family: ${FontFamilies.Regular};
  font-size: ${FontSizes.Smallest};
  letter-spacing: 0.04em;
  opacity: 0.85;
`;

const PassBody = styled.div`
  display: flex;
  flex-direction: column;

  @media only screen and ${MediaQueries.Md} {
    flex-direction: row;
    align-items: stretch;
  }
`;

const QrPane = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px 16px;
  border-bottom: 1px dashed ${Theme.Divider.Color};

  @media only screen and ${MediaQueries.Md} {
    width: 270px;
    flex-shrink: 0;
    border-bottom: none;
    border-right: 1px dashed ${Theme.Divider.Color};
  }
`;

const QrFrame = styled.div`
  display: inline-flex;
  padding: 12px;
  background: #ffffff;
`;

const QrImage = styled.img`
  width: 200px;
  height: 200px;
  object-fit: contain;
  display: block;
  background: #ffffff;
`;

const QrFallback = styled.p`
  width: 200px;
  min-height: 120px;
  margin: 0;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: #ffffff;
  color: #151719;
  border-radius: 8px;
  font-size: ${FontSizes.Smaller};
`;

const CodeLabel = styled.p`
  margin: 4px 0 0 0;
  font-size: ${FontSizes.Smallest};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${Theme.Text.Color};
  opacity: 0.6;
`;

const Code = styled.p`
  margin: 0;
  max-width: 210px;
  font-size: ${FontSizes.Smallest};
  line-height: 1.4;
  text-align: center;
  word-break: break-all;
  color: ${Theme.Text.Color};
`;

const DetailsPane = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  padding: 18px 20px;
`;

const PassName = styled.p`
  margin: 0;
  font-family: ${FontFamilies.Bold};
  font-size: ${FontSizes.Regular};
  color: ${Theme.Text.Color};
`;

const PassMeta = styled.p`
  margin: 0;
  font-size: ${FontSizes.Small};
  color: ${Theme.Text.Color};
  opacity: 0.8;
`;

const PassPrice = styled.p`
  margin: 2px 0 0 0;
  font-family: ${FontFamilies.Bold};
  font-size: ${FontSizes.Small};
  color: ${Theme.Text.Color};
  white-space: nowrap;
  flex-shrink: 0;
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const ItemCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const inFlightOrders = new Map();

const pad = (value) => String(value).padStart(2, '0');

const formatDateValue = (value, withTime) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const day = pad(date.getUTCDate());
  const year = date.getUTCFullYear();

  if (!withTime) {
    return `${day} ${MONTHS_SHORT[date.getUTCMonth()]} ${year}`;
  }

  const hours = date.getUTCHours();

  return `${day} ${MONTHS[date.getUTCMonth()]} ${year} - ${pad(hours)}:${pad(date.getUTCMinutes())} ${hours >= 12 ? 'PM' : 'AM'}`;
};

const eventDateLabel = (event) => {
  if (!event) {
    return '';
  }

  if (event.is_multi_event) {
    const start = formatDateValue(event.multi_event_start_date, false);
    const end = formatDateValue(event.multi_event_end_date, false);

    if (start && end) {
      return `${start} - ${end}`;
    }

    return start || end;
  }

  return formatDateValue(event.date, true);
};

const formatPrice = (price) => {
  if (price === null || price === undefined || price === '') {
    return '';
  }

  const amount = Number(price);

  if (Number.isNaN(amount)) {
    return '';
  }

  if (amount === 0) {
    return 'Free';
  }

  return `R${amount.toFixed(2)}`;
};

const ticketPriceLabel = (ticket) => {
  if (ticket.type === 'gift') {
    return 'Complimentary';
  }

  if (ticket.package_order_id) {
    return 'Package';
  }

  return formatPrice(ticket.price);
};

const asText = (value) => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return String(value);
  }

  return typeof value === 'string' ? value.trim() : '';
};

const seatLabel = (ticket) => {
  const block = asText(ticket.block_name);
  const seat = asText(ticket.seat_number);

  if (block && seat) {
    return `${block} · Seat ${seat}`;
  }

  if (seat) {
    return `Seat ${seat}`;
  }

  return block;
};

const recipientLabel = (ticket) => {
  if (!ticket.add_recipient_name_to_ticket) {
    return '';
  }

  return typeof ticket.recipient_name === 'string' ? ticket.recipient_name.trim() : '';
};

const formatItemName = (item) => {
  const name = item.product_name || 'Item';
  const option = item.option_value ? ` - ${item.option_value}` : '';
  const quantity = Number(item.quantity);

  if (quantity > 0) {
    return `${quantity} x ${name}${option}`;
  }

  return `${name}${option}`;
};

const readOrder = (payload) => {
  if (!payload || typeof payload !== 'object') {
    throw new Error('Failed to load tickets');
  }

  if (payload.isSuccess === false) {
    throw new Error(payload.message || 'Failed to load tickets');
  }

  const order = payload.event || payload.tickets || payload.vouchers
    ? payload
    : payload.data;

  if (!order || typeof order !== 'object') {
    throw new Error('Failed to load tickets');
  }

  return {
    orderId: order.order_id,
    event: order.event || {},
    tickets: Array.isArray(order.tickets) ? order.tickets : [],
    vouchers: Array.isArray(order.vouchers) ? order.vouchers : [],
  };
};

const fetchOrderTickets = (token) => {
  const existing = inFlightOrders.get(token);

  if (existing) {
    return existing;
  }

  const request = (async () => {
    const response = await fetch(`${Environment.apiHost}payment/order-tickets`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      throw new Error('Failed to load tickets');
    }

    return readOrder(await response.json());
  })();

  inFlightOrders.set(token, request);
  request.finally(() => {
    if (inFlightOrders.get(token) === request) {
      inFlightOrders.delete(token);
    }
  });

  return request;
};

const qrSrc = (value) => `${Environment.apiHost}payment/qr?value=${encodeURIComponent(value)}`;

const QrCode = ({ value, label, caption }) => {
  const [failed, setFailed] = useState(false);

  if (!value) {
    return <QrFallback>No code is available for this item.</QrFallback>;
  }

  return (
    <>
      {failed ? (
        <QrFallback>QR code unavailable. Use the code below.</QrFallback>
      ) : (
        <QrFrame>
          <QrImage
            src={qrSrc(value)}
            alt={label}
            width={200}
            height={200}
            onError={() => setFailed(true)}
          />
        </QrFrame>
      )}
      <CodeLabel>{caption}</CodeLabel>
      <Code>{value}</Code>
    </>
  );
};

const PassShell = ({ kind, index, total, code, codeLabel, caption, children }) => (
  <Pass>
    <PassHeader>
      <WeTicketLogo height={36} width={168} />
    </PassHeader>
    <KindBar $kind={kind}>
      {kind === 'voucher' ? 'Voucher' : 'Ticket'}
      {total > 1 && <KindCount>{index + 1} of {total}</KindCount>}
    </KindBar>
    <PassBody>
      <QrPane>
        <QrCode value={code} label={codeLabel} caption={caption} />
      </QrPane>
      <DetailsPane>
        {children}
      </DetailsPane>
    </PassBody>
  </Pass>
);

const TicketPass = ({ ticket, index, total }) => {
  const seat = seatLabel(ticket);
  const recipient = recipientLabel(ticket);
  const price = ticketPriceLabel(ticket);
  const title = ticket.event_price_title || 'Ticket';

  return (
    <PassShell
      kind={'ticket'}
      index={index}
      total={total}
      code={ticket.id}
      codeLabel={`QR code for ${title}`}
      caption={'Ticket ID'}>
      <PassName>{title}</PassName>
      {seat && <PassMeta>{seat}</PassMeta>}
      {recipient && <PassMeta>{recipient}</PassMeta>}
      {price && <PassPrice>{price}</PassPrice>}
    </PassShell>
  );
};

const VoucherPass = ({ voucher, index, total }) => {
  const items = Array.isArray(voucher.items) ? voucher.items : [];

  return (
    <PassShell
      kind={'voucher'}
      index={index}
      total={total}
      code={voucher.scan_id}
      codeLabel={'QR code for voucher'}
      caption={'Voucher ID'}>
      {items.length === 0 && <PassName>Voucher</PassName>}
      {items.length > 0 && (
        <ItemList>
          {items.map((item, itemIndex) => {
            const price = formatPrice(item.price);

            return (
              <ItemRow key={`${item.product_id || 'item'}-${item.product_variant_id || itemIndex}-${itemIndex}`}>
                <ItemCopy>
                  <PassName>{formatItemName(item)}</PassName>
                </ItemCopy>
                {price && <PassPrice>{price}</PassPrice>}
              </ItemRow>
            );
          })}
        </ItemList>
      )}
    </PassShell>
  );
};

const PurchasedTicketsComponent = (props) => {
  const [status, setStatus] = useState('loading');
  const [order, setOrder] = useState(null);
  const token = new URLSearchParams(props.common.location?.search || '').get('token');

  const loadTickets = async (isActive = () => true) => {
    if (!token) {
      if (isActive()) {
        setStatus('missing');
        props.common.hideLoading();
      }
      return;
    }

    if (isActive()) {
      setStatus('loading');
      props.common.showLoading('Loading your tickets...');
    }

    try {
      const nextOrder = await fetchOrderTickets(token);

      if (!isActive()) {
        return;
      }

      setOrder(nextOrder);
      setStatus('ready');
    } catch (error) {
      if (!isActive()) {
        return;
      }

      setStatus('error');
      props.common.showToast('Oh no, something went wrong, please try again.');
      Logger.error('PurchasedTickets', 'load', error);
    } finally {
      if (isActive()) {
        props.common.hideLoading();
      }
    }
  };

  useEffect(() => {
    let active = true;

    loadTickets(() => active);

    return () => {
      active = false;
    };
  }, []); // eslint-disable-line

  const event = order?.event;
  const tickets = order?.tickets || [];
  const vouchers = order?.vouchers || [];
  const hasTickets = tickets.length > 0;
  const hasVouchers = vouchers.length > 0;
  const dateLabel = eventDateLabel(event);
  const pageTitle = hasTickets || status !== 'ready' ? 'Your tickets' : 'Your order';

  return (
    <Container>
      <MaxWidthContainer>
        <Title>{pageTitle}</Title>
        <Divide />
        {status === 'missing' && (
          <Text
            display={'block'}
            fontSize={FontSizes.Small}
            padding={'0 0 20px 0'}>
            This link is missing a token.
          </Text>
        )}
        {status === 'error' && (
          <>
            <Text
              display={'block'}
              fontSize={FontSizes.Small}
              padding={'0 0 20px 0'}>
              We couldn&apos;t load your tickets. Please try again.
            </Text>
            <Button
              text={'Try again'}
              width={'100%'}
              fontFamily={FontFamilies.Regular}
              onClick={() => loadTickets()}
            />
          </>
        )}
        {status === 'ready' && order && (
          <>
            {event?.name && <EventName>{event.name}</EventName>}
            {event?.location_display && <EventMeta>{event.location_display}</EventMeta>}
            {dateLabel && <EventMeta>{dateLabel}</EventMeta>}
            {order.orderId && <EventMeta>Order #{order.orderId}</EventMeta>}
            {!hasTickets && !hasVouchers && (
              <Text
                display={'block'}
                fontSize={FontSizes.Small}
                padding={'16px 0 0 0'}>
                This order does not have any tickets to show.
              </Text>
            )}
            {hasTickets && (
              <>
                {hasVouchers && <SectionTitle>Tickets</SectionTitle>}
                <PassList $offset={!hasVouchers}>
                  {tickets.map((ticket, index) => (
                    <TicketPass
                      key={ticket.id || index}
                      ticket={ticket}
                      index={index}
                      total={tickets.length}
                    />
                  ))}
                </PassList>
              </>
            )}
            {hasVouchers && (
              <>
                <SectionTitle>Vouchers</SectionTitle>
                <PassList>
                  {vouchers.map((voucher, index) => (
                    <VoucherPass
                      key={voucher.scan_id || index}
                      voucher={voucher}
                      index={index}
                      total={vouchers.length}
                    />
                  ))}
                </PassList>
              </>
            )}
          </>
        )}
      </MaxWidthContainer>
    </Container>
  );
};

export const PurchasedTickets = withCommon(PurchasedTicketsComponent, {
  showHeader: false,
});
